import React, { useState } from 'react';
import Layout from '../../components/Layout';
import { useAuth } from '../../App';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Search, X, Download } from 'lucide-react';
import { SortableList, SortableItem } from '../../components/SortableList';

// Custom hooks
import { useDispatches } from './hooks';

// Components
import {
  DispatchCard,
  CreateDispatchModal,
  AssignTruckModal,
  TruckDataModal,
  StatusModal,
  EditDispatchModal,
  ExportDispatchesModal,
  EditTruckAssignmentModal
} from './components';
import GenerateLiquidacionModal from './components/GenerateLiquidacionModal';

// Utils
import { downloadDispatchExcel, downloadNominaExcel, downloadMultipleDispatchesExcel, CORREDOR_OPTIONS } from './utils';

/**
 * Página principal de Despachos - Versión Refactorizada
 * 
 * Permisos por rol:
 * - MASTER: Acceso completo (crear, editar, eliminar, ver precio)
 * - ADMIN: Descargas, asignar camión, editar asignación (sin precio, sin modificar/eliminar despacho)
 * - TRANSPORTISTA: Solo lectura de despachos donde tiene camiones asignados (sin precio, sin acciones)
 */
const Dispatches = () => {
  const { user: currentUser, token } = useAuth();
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [liquidacionModal, setLiquidacionModal] = useState({ open: false, dispatch: null });
  const fileInputRef = React.useRef(null);
  const [pendingPesaje, setPendingPesaje] = useState({ dispatchId: null, truckId: null });
  
  // Hook principal que maneja todo el estado y operaciones
  const {
    // Data
    dispatches,
    businesses,
    compras,
    importadoras,
    companies,
    loading,
    
    // UI State
    isDispatchExpanded,
    modals,
    selectedDispatch,
    selectedTruck,
    isModification,
    
    // Form State
    formData,
    setFormData,
    assignData,
    setAssignData,
    truckData,
    setTruckData,
    editData,
    setEditData,
    justification,
    setJustification,
    dispatchNumberError,
    
    // Actions
    openModal,
    closeModal,
    validateDispatchNumber,
    createDispatch,
    assignTruck,
    updateTruckData,
    changeStatus,
    updateDispatch,
    deleteDispatch,
    removeTruck,
    
    // Modal Openers
    openAssignModal,
    openAddTruckDataModal,
    openModifyTruckDataModal,
    openStatusModal,
    openEditDispatchModal,
    openEditTruckAssignmentModal,
    
    // Helpers
    toggleDispatch,
    getImportadoraName,
    getFilteredTrucks,
    getFilteredTrailers,
    getFilteredDrivers,
    
    // Filtros
    filters,
    setFilters,
    filteredDispatches,
    clearFilters,
    hasActiveFilters,
    
    // Reordenamiento
    reorderDispatches,
    reorderTrucks,
    
    // Pesajes de descarga
    uploadPesaje,
    viewPesaje,
    deletePesaje,
    
    // Assignment editing (for ADMIN)
    assignmentData,
    setAssignmentData,
    updateTruckAssignment,
    
    // Role checks
    isTransportista
  } = useDispatches(currentUser);

  // Determinar si puede hacer acciones
  const canTakeActions = currentUser.role === 'MASTER' || currentUser.role === 'ADMIN';

  // Handler para abrir el selector de archivo de pesaje
  const handleUploadPesajeClick = (dispatchId, truckId) => {
    setPendingPesaje({ dispatchId, truckId });
    fileInputRef.current?.click();
  };

  // Handler cuando se selecciona un archivo de pesaje
  const handleFileSelected = (e) => {
    const file = e.target.files?.[0];
    if (file && pendingPesaje.dispatchId && pendingPesaje.truckId) {
      uploadPesaje(pendingPesaje.dispatchId, pendingPesaje.truckId, file);
    }
    // Resetear el input para permitir seleccionar el mismo archivo de nuevo
    e.target.value = '';
    setPendingPesaje({ dispatchId: null, truckId: null });
  };

  return (
    <Layout>
      {/* Input oculto para subir archivos de pesaje */}
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept=".pdf,.png,.jpg,.jpeg"
        onChange={handleFileSelected}
      />
      
      <div className="space-y-3" data-testid="dispatches-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-heading font-semibold text-slate-800">
              {isTransportista ? 'Mis Despachos' : 'Despachos'}
            </h1>
            {isTransportista && (
              <p className="text-xs text-slate-500 mt-0.5">Despachos donde tu empresa presta servicio</p>
            )}
          </div>
          <div className="flex gap-2">
            {/* Botón exportar - Solo para MASTER y ADMIN */}
            {canTakeActions && (
              <Button 
                onClick={() => setIsExportModalOpen(true)} 
                variant="outline" 
                size="sm" 
                className="h-7 text-xs text-slate-600"
                data-testid="export-dispatches-button"
              >
                <Download size={14} className="mr-1" /> Exportar
              </Button>
            )}
            {currentUser.role === 'MASTER' && (
              <Button 
                onClick={() => openModal('create')} 
                data-testid="create-dispatch-button" 
                className="bg-slate-800 hover:bg-slate-900 text-white h-7 text-xs" 
                size="sm"
              >
                <Plus size={14} className="mr-1" /> Nuevo
              </Button>
            )}
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Search size={16} className="text-slate-400" />
              <span className="text-xs text-slate-600 font-medium">Filtrar:</span>
            </div>
            <Input
              type="date"
              placeholder="F. Carga"
              value={filters.loadDate}
              onChange={(e) => setFilters({ ...filters, loadDate: e.target.value })}
              className="h-7 text-xs w-32"
              title="Fecha de Carga"
            />
            <Input
              type="date"
              placeholder="F. Descarga"
              value={filters.deliveryDate}
              onChange={(e) => setFilters({ ...filters, deliveryDate: e.target.value })}
              className="h-7 text-xs w-32"
              title="Fecha de Descarga"
            />
            <Input
              placeholder="N° Despacho"
              value={filters.dispatchNumber}
              onChange={(e) => setFilters({ ...filters, dispatchNumber: e.target.value })}
              className="h-7 text-xs w-28"
            />
            <Select value={filters.importadoraId || "all"} onValueChange={(v) => setFilters({ ...filters, importadoraId: v === "all" ? "" : v })}>
              <SelectTrigger className="w-36 h-7 text-xs">
                <SelectValue placeholder="Importador" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {importadoras.map(i => (<SelectItem key={i.id} value={i.id}>{i.alias || i.razon_social}</SelectItem>))}
              </SelectContent>
            </Select>
            <Select value={filters.broker || "all"} onValueChange={(v) => setFilters({ ...filters, broker: v === "all" ? "" : v })}>
              <SelectTrigger className="w-28 h-7 text-xs">
                <SelectValue placeholder="Corredor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {CORREDOR_OPTIONS.map(o => (<SelectItem key={o} value={o}>{o}</SelectItem>))}
              </SelectContent>
            </Select>
            <Input
              placeholder="Destino"
              value={filters.destination}
              onChange={(e) => setFilters({ ...filters, destination: e.target.value })}
              className="h-7 text-xs w-24"
            />
            <Select value={filters.status || "all"} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
              <SelectTrigger className="w-28 h-7 text-xs">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="Pendiente">Pendiente</SelectItem>
                <SelectItem value="En Proceso">En Proceso</SelectItem>
                <SelectItem value="Finalizado">Finalizado</SelectItem>
              </SelectContent>
            </Select>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-500 h-7 text-xs px-2">
                <X size={14} className="mr-1" /> Limpiar
              </Button>
            )}
            <span className="text-xs text-slate-500 ml-auto">{filteredDispatches.length} de {dispatches.length}</span>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-slate-300 border-t-slate-600"></div>
          </div>
        ) : filteredDispatches.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded p-6 text-center">
            <p className="text-slate-400 text-sm">
              {hasActiveFilters ? 'No se encontraron despachos con los filtros aplicados' : 'No hay despachos registrados'}
            </p>
          </div>
        ) : (
          <div className="space-y-2 pl-6">
            <SortableList 
              items={filteredDispatches} 
              onReorder={reorderDispatches}
              disabled={hasActiveFilters || isTransportista}
            >
              {filteredDispatches.map((dispatch) => (
                <SortableItem key={dispatch.id} id={dispatch.id} disabled={hasActiveFilters || isTransportista}>
                  <DispatchCard
                    dispatch={dispatch}
                    isExpanded={isDispatchExpanded(dispatch.id)}
                    currentUser={currentUser}
                    getImportadoraName={getImportadoraName}
                    companies={companies}
                    onToggle={() => toggleDispatch(dispatch.id)}
                    onDownloadDispatch={downloadDispatchExcel}
                    onDownloadNomina={downloadNominaExcel}
                    onAssignTruck={openAssignModal}
                    onEditDispatch={openEditDispatchModal}
                    onChangeStatus={openStatusModal}
                    onDeleteDispatch={deleteDispatch}
                    onAddTruckData={openAddTruckDataModal}
                    onModifyTruckData={openModifyTruckDataModal}
                    onRemoveTruck={removeTruck}
                    onEditTruckAssignment={openEditTruckAssignmentModal}
                    onGenerateLiquidacion={(d) => setLiquidacionModal({ open: true, dispatch: d })}
                    onReorderTrucks={reorderTrucks}
                    onUploadPesaje={handleUploadPesajeClick}
                    onViewPesaje={viewPesaje}
                    onDeletePesaje={deletePesaje}
                  />
                </SortableItem>
              ))}
            </SortableList>
            {!hasActiveFilters && !isTransportista && filteredDispatches.length > 1 && (
              <p className="text-[10px] text-slate-400 text-center mt-2">
                Arrastra el icono ≡ a la izquierda para reordenar los despachos
              </p>
            )}
          </div>
        )}

        {/* Modals */}
        <CreateDispatchModal
          isOpen={modals.create}
          onClose={() => closeModal('create')}
          formData={formData}
          setFormData={setFormData}
          importadoras={importadoras}
          dispatchNumberError={dispatchNumberError}
          validateDispatchNumber={validateDispatchNumber}
          onSubmit={createDispatch}
        />

        <AssignTruckModal
          isOpen={modals.assign}
          onClose={() => closeModal('assign')}
          selectedDispatch={selectedDispatch}
          assignData={assignData}
          setAssignData={setAssignData}
          companies={companies}
          filteredTrucks={getFilteredTrucks(assignData.company_id)}
          filteredTrailers={getFilteredTrailers(assignData.company_id)}
          filteredDrivers={getFilteredDrivers(assignData.company_id)}
          businesses={businesses}
          compras={compras}
          onSubmit={assignTruck}
        />

        <TruckDataModal
          isOpen={modals.truckData}
          onClose={() => closeModal('truckData')}
          selectedTruck={selectedTruck}
          truckData={truckData}
          setTruckData={setTruckData}
          isModification={isModification}
          justification={justification}
          setJustification={setJustification}
          companies={companies}
          filteredTrucks={getFilteredTrucks(truckData.company_id)}
          filteredTrailers={getFilteredTrailers(truckData.company_id)}
          filteredDrivers={getFilteredDrivers(truckData.company_id)}
          businesses={businesses}
          compras={compras}
          onSubmit={updateTruckData}
        />

        <StatusModal
          isOpen={modals.status}
          onClose={() => closeModal('status')}
          selectedDispatch={selectedDispatch}
          editData={editData}
          setEditData={setEditData}
          justification={justification}
          setJustification={setJustification}
          onSubmit={changeStatus}
        />

        <EditDispatchModal
          isOpen={modals.editDispatch}
          onClose={() => closeModal('editDispatch')}
          selectedDispatch={selectedDispatch}
          formData={formData}
          setFormData={setFormData}
          importadoras={importadoras}
          justification={justification}
          setJustification={setJustification}
          onSubmit={updateDispatch}
        />

        <ExportDispatchesModal
          isOpen={isExportModalOpen}
          onClose={() => setIsExportModalOpen(false)}
          dispatches={dispatches}
          getImportadoraName={getImportadoraName}
          onExport={downloadMultipleDispatchesExcel}
        />

        {/* Modal para ADMIN editar asignación de camión */}
        <EditTruckAssignmentModal
          isOpen={modals.editTruckAssignment}
          onClose={() => closeModal('editTruckAssignment')}
          selectedTruck={selectedTruck}
          assignmentData={assignmentData}
          setAssignmentData={setAssignmentData}
          companies={companies}
          filteredTrucks={getFilteredTrucks(assignmentData.company_id)}
          filteredTrailers={getFilteredTrailers(assignmentData.company_id)}
          filteredDrivers={getFilteredDrivers(assignmentData.company_id)}
          onSubmit={updateTruckAssignment}
        />

        {/* Modal para generar liquidación */}
        {liquidacionModal.open && liquidacionModal.dispatch && (
          <GenerateLiquidacionModal
            dispatch={liquidacionModal.dispatch}
            token={token}
            onClose={() => setLiquidacionModal({ open: false, dispatch: null })}
            onSuccess={() => {
              toast.success('Liquidación generada exitosamente');
            }}
          />
        )}
      </div>
    </Layout>
  );
};

export default Dispatches;
